"use client";

import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { formatCzDate } from "@/lib/date";
import { BlogPost } from "@/types";
import { UserIcon } from "@heroicons/react/24/solid";
import Image from "next/image";
import Link from "next/link";
import ReactPlayer from "react-player";
import {Dialog, DialogContent} from "@/components/ui/Dialog";
import {useState} from "react";

const DEMO_IMAGES = [
  "/image/demo/demo1.PNG",
  "/image/demo/demo2.JPG",
  "/image/demo/demo3.JPG",
  "/image/demo/demo4.JPG",
  "/image/demo/demo5.JPG",
  "/image/demo/demo6.png",
  "/image/demo/demo7.png",
  "/image/demo/demo8.png",
  "/image/demo/demo9.png",
  "/image/demo/demo10.png",
  "/image/demo/demo11.png",
  "/image/demo/demo12.png",
  "/image/demo/demo13.png",
] as const;

function getDemoImage(postUrl: string) {
  const hash = Array.from(postUrl).reduce(
    (value, character) => (value * 31 + character.charCodeAt(0)) >>> 0,
    0,
  );

  return DEMO_IMAGES[hash % DEMO_IMAGES.length];
}

type BlogPostSidebarProps = {
  post: BlogPost;
  author: string;
  isLoggedIn: boolean;
  canUpgrade: boolean;
  upgradeMembershipLabel: string;
  createWatchdogLabel: string;
  onUpgradeMembership: () => void;
  relatedPosts: Array<{ url: string; title: string }>;
  relatedPostsBasePath?: string;
};

export function BlogPostSidebar({
  post,
  author,
  isLoggedIn,
  canUpgrade,
  upgradeMembershipLabel,
  createWatchdogLabel,
  onUpgradeMembership,
  relatedPosts,
  relatedPostsBasePath = "/pruvodce",
}: BlogPostSidebarProps) {
  const demoImage = getDemoImage(post.url);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Panel className="mt-10 w-full space-y-6 sm:mt-0 xl:w-1/3">
      <div className=" flex flex-col gap-4">
        <div className="flex items-center justify-between text-xs text-foreground/70 dark:text-foreground/70">
          <div className="flex items-center gap-1">
            <UserIcon className="size-4 text-foreground/70" />
            <span>{author}</span>
          </div>
          <span>
            {formatCzDate(post.date_modified, {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        </div>

        {(post._seo?.meta_keywords ?? []).length > 0 && (
          <div className="flex flex-wrap gap-3">
            {(post._seo?.meta_keywords ?? []).map((tag, idx) => (
              <span
                key={idx}
                className="rounded-full bg-hover px-3 py-1 text-xs text-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {isLoggedIn ? (
          canUpgrade && (
            <Button onClick={onUpgradeMembership} variant="default">
              {upgradeMembershipLabel}
            </Button>
          )
        ) : (
          <Button variant="default">
            <Link href={"/#cenik"}>{createWatchdogLabel}</Link>
          </Button>
        )}

        <>
          <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="relative aspect-[16/10] w-full cursor-zoom-in overflow-hidden rounded-2xl border border-softiq-border-subtle bg-softiq-surface-muted dark:border-softiq-border-dark dark:bg-softiq-dark-elevated"
          >
            <Image
                src={demoImage}
                alt="Ukázka aplikace SOFTIQ"
                fill
                sizes="(min-width: 1280px) 33vw, 100vw"
                className="object-contain p-3 transition-transform duration-300 hover:scale-[1.02]"
            />
          </button>

          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="flex max-w-6xl items-center justify-center border-0 bg-transparent p-0 shadow-none">
              <Image
                  src={demoImage}
                  alt="Ukázka aplikace SOFTIQ"
                  width={1600}
                  height={1000}
                  className="max-h-[90vh] w-auto rounded-xl object-contain"
              />
            </DialogContent>
          </Dialog>
        </>

        {post.key_takeaways ? (
          <div
            className="blog-post text-sm text-foreground"
            dangerouslySetInnerHTML={{ __html: post.key_takeaways }}
          />
        ) : null}
      </div>
      <div className=" relative aspect-video w-full overflow-hidden mt-0 sm:mt-3 mb-3 sm:mb-0 order-0 sm:order-1">
        <ReactPlayer
          src="https://youtu.be/9HRrkw_-BOM"
          width="100%"
          height="100%"
          controls
          playing={false}
          playsInline
        />
      </div>
      {relatedPosts.length ? (
        <nav aria-label="Související články" className="space-y-3">
          <h2 className="!text-left text-xl text-softiq-secondary">
            Související články
          </h2>
          {relatedPosts.map((relatedPost) => (
            <Link
              key={relatedPost.url}
              href={`${relatedPostsBasePath}/${relatedPost.url}`}
              className="block rounded-xl bg-hover p-4 font-medium text-foreground transition hover:text-softiq-secondary"
            >
              {relatedPost.title}
            </Link>
          ))}
        </nav>
      ) : null}
    </Panel>
  );
}
